import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import AttachmentModal from "./models/attchment";
import EmojiPickerPopover from "../common/EmojiPickerPopover";
import toast from "react-hot-toast";
import ShareMessageModal from "../common/ShareMessageModal";
import ViewOnceReceivedNotice from "../common/ViewOnceReceivedNotice";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import { resolveMessageCalendarDate } from "../../utils/resolveMessageCalendarDate";
import { LinkifiedText } from "../common/LinkifiedText";

/** Module-level spinner so parent re-renders do not recreate the component type. */
function RoundUploadSpinner({
    className = "h-12 w-12 border-4",
    trackClassName = "border-gray-300",
    headClassName = "border-t-[#7C4DFF]",
    label = "Uploading",
}) {
    return (
        <div
            className={`${className} ${trackClassName} ${headClassName} shrink-0 rounded-full border-solid animate-spin`}
            style={{ aspectRatio: "1 / 1", boxSizing: "border-box" }}
            aria-label={label}
            role="status"
        />
    );
}

function VideoUploadPlaceholder({ isUser, timeLabel }) {
    return (
        <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} group gap-2 overflow-visible`}>
            <div className={`rounded-2xl p-3 ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}`}>
                <div className="flex h-[220px] w-[320px] max-w-[420px] flex-col items-center justify-center rounded-[18px] border border-gray-200 bg-gray-50 px-6 text-center">
                    <RoundUploadSpinner label="Uploading video" />
                    <p className="mt-4 text-sm font-medium text-gray-700">Uploading...</p>
                </div>
                <div className="mt-2 text-end text-xs text-gray-600">{timeLabel}</div>
            </div>
        </div>
    );
}

// ---------- Voice Recorder UI (Input Bar Replacement) ----------
const VoiceRecorder = React.forwardRef(({ time, onMicClick, onCancel, onAttachmentClick, attachButtonRef }, ref) => {
    return (
        <div ref={ref} className="flex flex-col items-center justify-center w-full relative z-50">
            <div className="flex justify-around w-full max-w-[460px] absolute -top-12">
                <button
                    ref={attachButtonRef}
                    onClick={(e) => {
                        e.stopPropagation();
                        onAttachmentClick();
                    }}
                >
                    <img
                        src="../chatscreen/document.svg"
                        alt="attachment"
                        className="w-[25px] h-[25px]"
                    />
                </button>
                <button onClick={(e) => {
                    e.stopPropagation();
                    onMicClick();
                }}>
                    <img
                        src="../chatscreen/recording-mic.svg"
                        alt="mic"
                        className="w-[56px] h-[56px]"
                    />
                </button>
                <button onClick={(e) => {
                    e.stopPropagation();
                    onCancel();
                }}>
                    <img
                        src="../chatscreen/message-chat.svg"
                        alt="message"
                        className="w-[25px] h-[25px]"
                    />
                </button>
            </div>
        </div>
    );
});

// Message Menu Component (for hover actions)
function MessageMenu({ message, onShare, onDelete }) {
    const shareType = message.shareType || message.original?.share_type || "";
    const isViewOnce = shareType === "sharetoview";
    const isShareable = !isViewOnce;

    if (!isShareable) return null;

    return (
        <div className="flex items-center gap-2 h-max bg-white rounded-full shadow-lg border border-gray-200 p-2">
            <button
                onClick={onShare}
                title="Share message"
                className="p-0 hover:bg-purple-50 rounded-lg transition text-gray-600 hover:text-[#7C4DFF]"
            >
                <svg fill="#000000" width="15px" height="15px" viewBox="0 0 24 24" id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"><path d="M21.707,11.293l-8-8A.99991.99991,0,0,0,12,4V7.54492A11.01525,11.01525,0,0,0,2,18.5V20a1,1,0,0,0,1.78418.62061,11.45625,11.45625,0,0,1,7.88672-4.04932c.0498-.00635.1748-.01611.3291-.02588V20a.99991.99991,0,0,0,1.707.707l8-8A.99962.99962,0,0,0,21.707,11.293ZM14,17.58594V15.5a.99974.99974,0,0,0-1-1c-.25488,0-1.2959.04932-1.56152.085A14.00507,14.00507,0,0,0,4.05176,17.5332,9.01266,9.01266,0,0,1,13,9.5a.99974.99974,0,0,0,1-1V6.41406L19.58594,12Z"/></svg>
            </button>
            {/* {message.isUser && (
                <button
                    onClick={onDelete}
                    title="Delete message"
                    className="p-2 hover:bg-red-50 rounded-lg transition text-gray-600 hover:text-red-600"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            )} */}
        </div>
    );
}
export default function ChatMessagesArea({
    messages,
    contact,
    status,
    onBackMobile,
    showVoice,
    voiceTime,
    onMicButton,
    onSendVoiceClick,
    onReturnToInput,
    showAttachModal,
    setShowAttachModal,
    handleAttachPick,
    showVoiceAttachModal,
    setShowVoiceAttachModal,
    message,
    setMessage,
    onSendMessage,
    uploadProgress,
    isUploading,
    fileInputRef,
    selectedContactId,
    onFileSelect,
    markContactRead,
    allContacts = [],
    onShareMessage,
    currentUserId,
    onViewOnceImage
}) {
    const rootRef = useRef();
    const messagesScrollRef = useRef();
    const emojiRef = useRef();
    const emojiButtonRef = useRef();
    const chatInputRef = useRef();
    const [showEmoji, setShowEmoji] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(
        () => typeof window !== "undefined" && window.innerWidth < 768
    );
    const [imagePreview, setImagePreview] = useState(null);
    const [viewOncePreview, setViewOncePreview] = useState(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [messageToShare, setMessageToShare] = useState(null);
    const [videoModal, setVideoModal] = useState(null); // { src, poster }
    const [isFileDragging, setIsFileDragging] = useState(false);
    const dragDepthRef = useRef(0);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = () => setIsMobileLayout(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    const handleEmojiToggle = useCallback((e) => {
        if (isMobileLayout) {
            e.preventDefault();
            chatInputRef.current?.blur();
        }
        setShowEmoji((s) => !s);
    }, [isMobileLayout]);

    useEffect(() => {
        if (isMobileLayout && showEmoji) {
            chatInputRef.current?.blur();
        }
    }, [isMobileLayout, showEmoji]);

    const asRenderableText = (value, fallback = "") => {
        if (value === null || value === undefined) return fallback;
        if (typeof value === "string") return value;
        if (typeof value === "number" || typeof value === "boolean") return String(value);
        if (typeof value === "object" && !Array.isArray(value)) {
            if (value.ciphertext && value.nonce) return "[Encrypted message]";
            try {
                return JSON.stringify(value);
            } catch {
                return fallback;
            }
        }
        return fallback;
    };

    const formatFileSize = (val) => {
        if (val === undefined || val === null || val === "") return "";
        // If already a formatted string like '2.4 MB' or '300 KB', return as-is
        if (typeof val === 'string') {
            const s = val.trim();
            if (/\d+(\.\d+)?\s*(MB|GB|KB|B|bytes|mb|gb|kb|b)$/.test(s)) return s;
            const n = Number(s);
            if (!isNaN(n)) val = n;
            else return s;
        }
        const bytes = Number(val);
        if (isNaN(bytes)) return "";
        const mb = bytes / (1024 * 1024);
        if (mb >= 1) return `${mb.toFixed(2)} MB`;
        const kb = bytes / 1024;
        return `${kb.toFixed(1)} KB`;
    };

    const attachButtonRef = useRef();
    const voiceAttachButtonRef = useRef();
    const voiceRecorderRef = useRef();
    const messagesEndRef = useRef();
    const modalVideoRef = useRef(null);
    const [modalVideoPlaying, setModalVideoPlaying] = useState(false);
    const [modalVideoCurrent, setModalVideoCurrent] = useState(0);
    const [modalVideoDuration, setModalVideoDuration] = useState(0);
    const [modalVideoVolume, setModalVideoVolume] = useState(1);
    const [modalVideoMuted, setModalVideoMuted] = useState(false);
    const safeStatus = asRenderableText(status, "");
    const handleShareMessage = (msg) => {
        setMessageToShare(msg);
        setShareModalOpen(true);
    };

    const handleDeleteMessage = (messageId) => {
        toast.success("Message deleted");
    };

    const renderCompactViewOncePill = (label, message, isUser, isUploading = false, onShare, onDelete) => (
        <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
            <div className={`max-w-xs rounded-xl px-3 py-2 ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}`}>
                <div className="flex items-center gap-2">
                    {isUploading ? (
                        <div className="flex h-6 w-6 items-center justify-center">
                            <div className="h-4 w-4 border-2 border-[#7C4DFF]/30 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                        </div>
                    ) : (
                        <div className="h-6 w-6 rounded-full border-2 border-dashed border-[#7C4DFF] flex items-center justify-center text-[10px] font-semibold text-[#7C4DFF]">
                            1
                        </div>
                    )}
                    <span className="text-sm font-medium text-gray-900">{label}</span>
                    <span className="text-xs text-gray-500">{formatMessageTime(message?.original?.created_at || message.time)}</span>
                </div>
            </div>
            <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
            </div>
        </div>
    );

    const [blurViewOnce, setBlurViewOnce] = useState(false);

    useEffect(() => {
        if (!viewOncePreview && !videoModal?.isViewOnce) return;
        setBlurViewOnce(false);

        const handleVisibility = () => {
            setBlurViewOnce(document.hidden);
        };
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [viewOncePreview, videoModal?.isViewOnce]);

    // Sync modal video element events when videoModal opens
    useEffect(() => {
        const v = modalVideoRef.current;
        if (!videoModal || !v) return;

        const onTime = () => {
            setModalVideoCurrent(v.currentTime || 0);
        };
        const onPlay = () => setModalVideoPlaying(true);
        const onPause = () => setModalVideoPlaying(false);
        const onLoaded = () => setModalVideoDuration(v.duration && isFinite(v.duration) ? v.duration : 0);

        v.addEventListener('timeupdate', onTime);
        v.addEventListener('play', onPlay);
        v.addEventListener('pause', onPause);
        v.addEventListener('loadedmetadata', onLoaded);

        // Try autoplay in modal
        const t = setTimeout(() => {
            try { v.play().catch(() => {}); } catch (_) {}
        }, 50);

        return () => {
            clearTimeout(t);
            try {
                v.removeEventListener('timeupdate', onTime);
                v.removeEventListener('play', onPlay);
                v.removeEventListener('pause', onPause);
                v.removeEventListener('loadedmetadata', onLoaded);
            } catch (_) {}
        };
    }, [videoModal]);

    // ---------- Message Components ----------

    // Format message time: show only time and UTC offset, e.g. "05:30 PM (UTC+05:30)"
    const formatMessageTime = (raw) => {
        if (!raw && raw !== 0) return "";
        try {
            // Try to create a Date. Accept ISO strings, timestamps, or already formatted strings.
            const d = typeof raw === "number" ? new Date(raw) : new Date(raw);
            if (isNaN(d.getTime())) return String(raw);

            const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
            const offsetMinutes = -d.getTimezoneOffset(); // minutes offset from UTC
            const sign = offsetMinutes >= 0 ? "+" : "-";
            const abs = Math.abs(offsetMinutes);
            const hh = String(Math.floor(abs / 60)).padStart(2, "0");
            const mm = String(abs % 60).padStart(2, "0");
            return `${timeStr}`;
        } catch (e) {
            return String(raw);
        }
    };

    // Format date divider (Today / Yesterday / date)
    const formatDateDivider = (date) => {
        if (!date) return "";
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = date.getDate() === today.getDate() && 
                        date.getMonth() === today.getMonth() && 
                        date.getFullYear() === today.getFullYear();
        const isYesterday = date.getDate() === yesterday.getDate() && 
                            date.getMonth() === yesterday.getMonth() && 
                            date.getFullYear() === yesterday.getFullYear();

        if (isToday) return "Today";
        if (isYesterday) return "Yesterday";
        
        return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    };

    function TextMessage({ message, isUser, onShare, onDelete }) {
        const messageText = asRenderableText(message?.text, "");
        return (
            <div className={`relative flex ${isUser ? "justify-end flex-row-reverse" : "justify-start"} group gap-2 items-center overflow-visible`}>
                <div
                    className={`
          relative
          max-w-xs lg:max-w-md
          px-4 py-2 pr-20
          rounded-xl
          text-black
          ${isUser ? "bg-[#EDD6FE]" : "bg-white border border-gray-200"}
        `}
                >
                    {/* Message text */}
                    <p className="text-sm font-medium break-words whitespace-pre-wrap">
                        <LinkifiedText text={messageText} />
                    </p>

                    {/* Time */}
                    <span
                        className="
            absolute
            bottom-2
            right-3
            text-xs
            text-gray-600
            whitespace-nowrap
          "
                    >
                        {formatMessageTime(message?.original?.created_at || message.time)}
                    </span>
                </div>
                <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                    <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
                </div>
            </div>
        );
    }
    function ImageMessage({ message, isUser, onShare, onDelete }) {
        const [imageError, setImageError] = useState(false);
        const [isLoaded, setIsLoaded] = useState(false);

        const shareType = message.shareType || message.original?.share_type || "";
        const viewedBy = message.viewedBy || message.original?.viewed_by || [];
        const isViewOnce = shareType === "sharetoview";
        const hasViewed = !isUser && isViewOnce && Array.isArray(viewedBy)
            ? viewedBy.some((id) => String(id) === String(currentUserId))
            : false;

        const hasFileUrl = Boolean(message.fileUrl);
        const resolvedUrl = hasFileUrl ? resolveMediaUrl(message.fileUrl) : null;
        const fallbackUrl = "../chatscreen/image-file.jpg";
        const isUploading = Boolean(message.uploading);
        const showLoaderPlaceholder = isUploading && (!resolvedUrl || !isLoaded) && !imageError;
        const isDeleted = Boolean(message.fileDeleted || message.original?.file_deleted);
        const shouldHideImage = isViewOnce && (isUser || hasViewed || isDeleted);
        const showViewOnceNotice = isViewOnce && !isUser && !hasViewed && !isDeleted && !isUploading;
        const showCompactPill = isViewOnce && shouldHideImage;

        if (showViewOnceNotice) {
            return (
                <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
                    <ViewOnceReceivedNotice
                        time={formatMessageTime(message?.original?.created_at || message.time)}
                    />
                    <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                        <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
                    </div>
                </div>
            );
        }

        if (showCompactPill) {
            return renderCompactViewOncePill("Photo", message, isUser, isUploading, onShare, onDelete);
        }

        return (
            <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
            <div
                className={`max-w-md rounded-xl p-2 ${isUser ? "bg-[#EDD6FE] text-white" : "bg-white border border-gray-200"}`}
            >
                <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center gap-3">
                        <div className={`flex-shrink-0 relative w-48 h-48`}>
                            {imageError ? (
                                <div className="w-full h-full bg-gray-100 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-12 w-12 text-gray-400 mb-2"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <p className="text-xs text-gray-500 text-center px-4">Image not available</p>
                                </div>
                            ) : showLoaderPlaceholder ? (
                                <div className="w-full h-full rounded-xl bg-gray-100 flex flex-col items-center justify-center border border-gray-200 animate-pulse">
                                    <div className="w-10 h-10 border-4 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                                    <p className="text-xs text-gray-500 mt-3">Uploading...</p>
                                </div>
                            ) : (
                                <>
                                    <img
                                        src={resolvedUrl || fallbackUrl}
                                        alt="Shared content"
                                        className="w-full h-full object-cover rounded-xl transition-opacity cursor-pointer hover:opacity-80"
                                        onClick={() => {
                                            if (!imageError) setImagePreview(resolvedUrl || fallbackUrl);
                                        }}
                                        onLoad={() => setIsLoaded(true)}
                                        onError={() => setImageError(true)}
                                    />
                                    {isUploading && (
                                        <div className="absolute inset-0 rounded-xl bg-black/30 flex flex-col items-center justify-center">
                                            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" aria-label="Uploading" />
                                            <p className="text-xs text-white mt-2">Uploading...</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            {isUploading && !showLoaderPlaceholder && (
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                                    <span className="text-xs text-gray-500">Uploading...</span>
                                    <button
                                        className="ml-1 p-1 hover:bg-gray-100 rounded"
                                        title="Cancel upload"
                                        onClick={message.onCancelUpload}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-xs text-gray-600 text-end mt-1">{formatMessageTime(message?.original?.created_at || message.time)}</div>
            </div>
            <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
            </div>
            </div>
        );
    }

    function DocumentMessage({ message, isUser, onShare, onDelete }) {
        // Construct download URL if fileUrl is available
        const downloadUrl = message.fileUrl
            ? (message.fileUrl.startsWith('http')
                ? message.fileUrl
                : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`)
            : null;

        return (
            <div className={`relative flex ${isUser ? "justify-end flex-row-reverse" : "justify-start"} group gap-2 items-center overflow-visible`}>
            <div
                className={`max-w-xs lg:max-w-md rounded-2xl p-3 ${isUser
                    ? "bg-[#EDD6FE] text-white"
                    : "bg-white text-gray-900 border border-gray-200"
                    }`}
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg">
                        <img
                            src="../chatscreen/doc.svg"
                            alt="Document"
                            className="w-8 h-8"
                        />
                    </div>
                    <div className="flex-1">
                        <p className={`text-sm font-medium truncate max-w-[200px] text-black`}>
                            {message.fileName || message.text || "Document"}
                        </p>
                        <p className={`text-xs text-gray-600`}>
                            {formatFileSize(message.fileSize || message.original?.file_size || message.file_size)}
                        </p>
                        {message.uploading && (
                            <div className="flex items-center gap-2 mt-1">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-[#7C4DFF] rounded-full animate-spin" aria-label="Uploading" />
                                <span className="text-xs text-gray-500">Uploading...</span>
                                <button
                                    className="ml-1 p-1 hover:bg-gray-100 rounded"
                                    title="Cancel upload"
                                    onClick={message.onCancelUpload}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        )}
                        {message.uploadFailed && (
                            <p className="text-xs text-red-600 mt-1 break-words">
                                Upload failed: {message.uploadError || "Unknown error"}
                            </p>
                        )}
                    </div>
                    {downloadUrl && !message.uploading && (
                        <a
                            href={downloadUrl}
                            target="blank"
                            download={message.fileName}
                            className="p-1 rounded hover:bg-gray-100 hover:rounded-lg"
                        >
                            <img
                                src="../chatscreen/download.svg"
                                alt="Download"
                                className="w-5 h-5"
                            />
                        </a>
                    )}
                </div>
                <p className={`text-xs  text-end text-gray-600`}>{formatMessageTime(message?.original?.created_at || message.time)}</p>
            </div>
            <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
            </div>
            </div>
        );
    }

    function AudioMessage({ message, isUser, onShare, onDelete }) {
        const [isPlaying, setIsPlaying] = useState(false);
        const [currentTime, setCurrentTime] = useState(0);
        const [duration, setDuration] = useState(0);
        const [isHovering, setIsHovering] = useState(false);
        const audioRef = useRef(null);

        // Format time as mm:ss
        const formatTime = (seconds) => {
            if (!isFinite(seconds) || seconds < 0) return "0:00";
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, "0")}`;
        };

        // Update duration on metadata load and when src changes
        useEffect(() => {
            const audio = audioRef.current;
            if (!audio) return;
            let triedSeek = false;
            const updateDuration = () => {
                if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration);
                    } else if (!triedSeek && message.fileUrl && message.fileUrl.endsWith('.webm')) {
                    // Try to force duration calculation for webm
                    triedSeek = true;
                    const originalTime = audio.currentTime;
                    // Seek to a very large time to trigger duration
                    try {
                        audio.currentTime = 1e10;
                    } catch (err) {
                        // If element was removed or seeking failed, ignore
                        }
                    // After durationchange, seek back
                    const resetTime = () => {
                        try {
                            audio.currentTime = originalTime || 0;
                        } catch (err) {
                            }
                        audio.removeEventListener('durationchange', resetTime);
                    };
                    audio.addEventListener('durationchange', resetTime);
                }
            };
            audio.addEventListener("loadedmetadata", updateDuration);
            audio.addEventListener("durationchange", updateDuration);
            // Fallback: try to set duration after a short delay if not available immediately
            const fallback = setTimeout(() => {
                updateDuration();
            }, 700);
            return () => {
                audio.removeEventListener("loadedmetadata", updateDuration);
                audio.removeEventListener("durationchange", updateDuration);
                clearTimeout(fallback);
            };
        }, [message.fileUrl]);

        // Keep currentTime in sync with audio element
        useEffect(() => {
            const audio = audioRef.current;
            if (!audio) return;
            const updateTime = () => setCurrentTime(audio.currentTime);
            audio.addEventListener("timeupdate", updateTime);
            // Also update duration on play in case it was not set
            const updateDurationOnPlay = () => {
                if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration);
                }
            };
            audio.addEventListener("play", updateDurationOnPlay);
            return () => {
                audio.removeEventListener("timeupdate", updateTime);
                audio.removeEventListener("play", updateDurationOnPlay);
            };
        }, []);
        const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

        // Play/pause logic with robust promise handling to avoid
        // "The play() request was interrupted because the media was removed" errors
        const handlePlayClick = async () => {
            const audio = audioRef.current;
            if (!audio) return;

            if (isPlaying) {
                try {
                    audio.pause();
                } catch (err) {
                    // ignore pause errors
                    }
                setIsPlaying(false);
                return;
            }

            try {
                const playPromise = audio.play();
                if (playPromise && typeof playPromise.then === 'function') {
                    await playPromise;
                }
                // If play succeeded, state will also be updated by onPlay handler,
                // but set it here defensively in case the event doesn't fire.
                setIsPlaying(true);
            } catch (err) {
                // Play was interrupted (element removed, autoplay blocked, etc.).
                // Log and ensure we don't leave UI in a 'playing' state.
                setIsPlaying(false);
            }
        };

        // Ensure audio is paused and cleaned up when component unmounts
        useEffect(() => {
            return () => {
                const audio = audioRef.current;
                if (audio) {
                    try {
                        audio.pause();
                        // Resetting time can help avoid strange states
                        audio.currentTime = 0;
                    } catch (err) {
                        // ignore cleanup errors
                        }
                }
            };
        }, []);
        return (
            <div className={`relative flex flex-col ${isUser ? 'justify-end' : 'justify-start'} group gap-2 overflow-visible`}>
                <div
                    className={`max-w-xs min-w-[150px] lg:max-w-md rounded-2xl p-4 text-black ${isUser
                        ? "bg-[#EDD6FE]"
                        : "bg-white  border border-gray-200"
                        }`}
                >
                    {/* Play Button and Audio Wave */}
                    <div className="gap-3 mb-3">
                        <button
                            onClick={handlePlayClick}
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                            className={`flex-shrink-0 p-2 rounded-full transition ${isUser ? "hover:bg-[#6938EF]" : "hover:bg-gray-100"}`}
                        >
                            <img
                                src={
                                    isPlaying
                                        ? ("../chatscreen/pause.svg")
                                        : (isHovering && isUser ? "../chatscreen/volume-white.svg" : "../chatscreen/speaker.svg")
                                }
                                alt={isPlaying ? "Pause" : "Play"}
                                className="w-8 h-8"
                            />
                        </button>
                        <img
                            src="../chatscreen/audio-wave.svg"
                            alt="Audio wave"
                            className="flex-1 h-10"
                        />
                    </div>

                    {/* Duration Display */}
                    <div className="text-base font-medium mb-1">
                        {formatTime(duration)}
                    </div>

                    {/* Message Time */}
                    <p className={`text-xs text-end text-gray-600`}>
                        {formatMessageTime(message?.original?.created_at || message.time)}
                    </p>
                    <audio
                        ref={audioRef}
                        src={
                            message.fileUrl?.startsWith("http")
                                ? message.fileUrl
                                : `${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`
                        }
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        className="hidden"
                    />
                    {/* <input
                           type="range"
                           min={0}
                           max={duration || 0}
                           step="0.01"
                           value={currentTime}
                           onChange={(e) => {
                               const newTime = parseFloat(e.target.value);
                               setCurrentTime(newTime);
                               if (audioRef.current) audioRef.current.currentTime = newTime;
                           }}
                           className="audio-slider w-full h-4 bg-transparent cursor-pointer text-[#5D5FEF]"
                       /> */}
                </div>
                <div className="flex items-center mb-3 mt-3">
                    <span className={`text-xs font-medium min-w-[35px] text-[#5D5FEF]`}>
                        {formatTime(currentTime)}
                    </span>
                    <div className="flex-1 relative h-4 flex items-center">
                        {/* Background Bar */}
                        <div className={`w-full rounded-full h-2 ${isUser ? "bg-purple-400" : "bg-gray-300"}`}></div>
                        {/* Filled Progress */}
                        <div
                            className={`absolute left-0 top-1/2 -translate-y-1/2 rounded-full h-2  bg-[#5D5FEF]`}
                            style={{ width: `${progress}%` }}
                        ></div>
                        {/* Styled slider */}
                        <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            value={currentTime}
                            step="0.01"
                            onChange={(e) => {
                                const newTime = parseFloat(e.target.value);
                                setCurrentTime(newTime);
                                if (audioRef.current) audioRef.current.currentTime = newTime;
                            }}
                            className="audio-slider absolute w-full h-4 bg-transparent cursor-pointer text=[#5D5FEF]"
                        />
                    </div>
                    <span className={`text-xs font-medium min-w-[35px] text-right text-[#5D5FEF]`}>
                        {formatTime(duration)}
                    </span>
                </div>
            </div>
        );
    }

    function VideoMessage({ message, isUser, onShare, onDelete }) {
        const [thumbError, setThumbError] = useState(false);
        const isUploading = Boolean(message.uploading);
        const uploadFailed = Boolean(message.uploadFailed);
        const shareType = message.shareType || message.original?.share_type || "";
        const viewedBy = message.viewedBy || message.original?.viewed_by || [];
        const isViewOnce = shareType === "sharetoview";
        const serverViewed = isViewOnce && Array.isArray(viewedBy)
            ? viewedBy.some((id) => String(id) === String(currentUserId))
            : false;
        const hasFileUrl = Boolean(message.fileUrl);
        const isDeleted = Boolean(message.fileDeleted || message.original?.file_deleted);
        const shouldHideVideo = isViewOnce && (isUser || serverViewed || isDeleted);
        const showViewOnceNotice =
            isViewOnce && !isUser && !serverViewed && !isDeleted && !isUploading && !uploadFailed;

        // Resolve src and poster thumbnail if available
        const src = resolveMediaUrl(message.fileUrl);

        const rawThumbnail =
            message.thumbnail_image ||
            message.thumbnailImage ||
            message.thumbnailUrl ||
            message.thumbnail ||
            message.previewImage ||
            message.original?.thumbnail_image ||
            message.original?.thumbnail_path ||
            null;
        const poster = rawThumbnail
            ? (String(rawThumbnail).startsWith("http")
                ? rawThumbnail
                : `${process.env.REACT_APP_API_BASE_URL}${String(rawThumbnail).startsWith("/") ? rawThumbnail : `/${rawThumbnail}`}`)
            : null;

        const openInModalAndPlay = (e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            if (!src || shouldHideVideo || showViewOnceNotice) return;
            setVideoModal({ src, poster, isViewOnce: false });
        };

        if (!isUploading && !uploadFailed && showViewOnceNotice) {
            return (
                <div className={`relative flex items-center ${isUser ? "flex-row-reverse" : "flex-row"} gap-2 group overflow-visible`}>
                    <ViewOnceReceivedNotice
                        time={formatMessageTime(message?.original?.created_at || message.time)}
                    />
                    <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} z-20 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto`}>
                        <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
                    </div>
                </div>
            );
        }

        if (!uploadFailed && isViewOnce && shouldHideVideo) {
            return renderCompactViewOncePill("Video", message, isUser, isUploading, onShare, onDelete);
        }

        const renderThumbnail = () => {
            if (poster && !thumbError) {
                return (
                    <img
                        src={poster}
                        alt="Video thumbnail"
                        className="h-full w-full object-cover"
                        onError={() => setThumbError(true)}
                    />
                );
            }

            return (
                <div className="h-full w-full bg-gradient-to-br from-[#3B3B4A] via-[#23232F] to-[#111118]" />
            );
        };

        if (isUploading && !(isViewOnce && isUser)) {
            return (
                <VideoUploadPlaceholder
                    isUser={isUser}
                    timeLabel={formatMessageTime(message?.original?.created_at || message.time)}
                />
            );
        }

        if (uploadFailed) {
            return (
                <div className={`relative flex items-center ${isUser ? 'flex-row-reverse' : 'flex-row'} group gap-2 overflow-visible`}>
                    <div className={`rounded-2xl p-3 ${isUser ? 'bg-[#EDD6FE]' : 'bg-white border border-gray-200'}`}>
                        <div className="w-[320px] max-w-[420px] min-h-[160px] rounded-[18px] bg-red-50 border border-red-200 flex flex-col items-center justify-center text-center px-6 py-5">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-800">{message.fileName || "Video"}</p>
                            <p className="text-xs text-red-600 mt-2 break-words">
                                {message.uploadError || "Video upload failed"}
                            </p>
                        </div>
                        <div className="text-xs text-gray-600 text-end mt-2">{formatMessageTime(message?.original?.created_at || message.time)}</div>
                    </div>
                    <div className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} transition-opacity duration-150 z-20 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto`}>
                        <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
                    </div>
                </div>
            );
        }

        return (
            <div className={`relative flex items-center ${isUser ? 'flex-row-reverse' : 'flex-row'} group gap-2 overflow-visible`}> 
                <div className={`rounded-2xl p-2 ${isUser ? 'bg-[#EDD6FE]' : 'bg-white border border-gray-200'}`}>
                    <div
                        className="relative w-80 max-w-[420px] overflow-hidden rounded-[18px] bg-black shadow-lg"
                        style={{ aspectRatio: '16 / 9', maxHeight: '220px' }}
                        onClick={openInModalAndPlay}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openInModalAndPlay(e); }}
                    >
                        <div className="absolute inset-0">
                            {renderThumbnail()}
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/20" />

                        <button
                            onClick={(e) => { e.stopPropagation(); openInModalAndPlay(e); }}
                            aria-label={"Play video"}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition-transform duration-200 hover:scale-105"
                        >
                            <span className="ml-1 inline-block border-l-[14px] border-y-[10px] border-y-transparent border-l-[#22223a]" />
                        </button>
                    </div>

                    <div className="text-xs text-gray-600 text-end mt-2">{formatMessageTime(message?.original?.created_at || message.time)}</div>
                </div>
                <div
                    className={`absolute top-1/2 -translate-y-1/2 ${isUser ? "right-full mr-2" : "left-full ml-2"} transition-opacity duration-150 z-20 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto`}
                >
                        <MessageMenu message={message} onShare={onShare} onDelete={onDelete} />
                </div>
            </div>
        );
    }

    // ---------- Main Message Renderer ----------
    function MessageRenderer({ message }) {
        const isUser = message.isUser;
        const onShare = () => handleShareMessage(message);
        const onDelete = () => handleDeleteMessage(message.id);
        const resolvedMime = String(
            message.mime ||
            message.mimeType ||
            message.original?.mime ||
            message.original?.mime_type ||
            ""
        ).toLowerCase();
        const resolvedFileUrl = message.fileUrl || message.original?.file_url || message.original?.file || "";
        const fileName = message.fileName || message.original?.file_name || message.text || "";
        const fileHint = `${resolvedFileUrl} ${fileName}`.toLowerCase();

        const messageType = (() => {
            if (message.type && message.type !== "file" && message.type !== "text") return message.type;

            if (resolvedMime.startsWith("image")) return "image";
            if (resolvedMime.startsWith("audio")) return "audio";
            if (resolvedMime.startsWith("video")) return "video";

            if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(fileHint)) return "image";
            if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i.test(fileHint)) return "audio";
            if (/\.(mp4|webm|mov|avi|mkv|m4v)(\?.*)?$/i.test(fileHint)) return "video";

            if (message.type === "document" || resolvedFileUrl || message.fileName) return "document";
            return message.type || "text";
        })();

        // Video upload placeholder is a stable module component — render it here so nested
        // VideoMessage remounts (from progress / parent state) do not restart the spinner.
        if (messageType === "video") {
            const isUploading = Boolean(message.uploading);
            const shareType = message.shareType || message.original?.share_type || "";
            const isViewOnce = shareType === "sharetoview";
            if (isUploading && !(isViewOnce && isUser)) {
                return (
                    <VideoUploadPlaceholder
                        isUser={isUser}
                        timeLabel={formatMessageTime(message?.original?.created_at || message.time)}
                    />
                );
            }
        }

        switch (messageType) {
            case "image":
                return <ImageMessage message={message} isUser={isUser} onShare={onShare} onDelete={onDelete} />;
            case "document":
                return <DocumentMessage message={message} isUser={isUser} onShare={onShare} onDelete={onDelete} />;
            case "video":
                return <VideoMessage message={message} isUser={isUser} onShare={onShare} onDelete={onDelete} />;
            case "audio":
                return <AudioMessage message={message} isUser={isUser} onShare={onShare} onDelete={onDelete} />;
            default:
                return <TextMessage message={message} isUser={isUser} onShare={onShare} onDelete={onDelete} />;
        }
    }

    const renderedMessages = useMemo(() => {
        return messages?.map((message, index) => {
            const currentDate = resolveMessageCalendarDate(message);
            const previousMessage = index > 0 ? messages[index - 1] : null;
            const previousDate = previousMessage ? resolveMessageCalendarDate(previousMessage) : null;
            const showDateDivider = !previousDate || (currentDate && previousDate && currentDate.getTime() !== previousDate.getTime());
            const messageKey = message?.id || message?.tempId || message?._clientUploadId || `msg-${index}`;

            return (
                <React.Fragment key={messageKey}>
                    {showDateDivider && currentDate && (
                        <div className="flex justify-center items-center my-6">
                            <div className="bg-gray-300/60 px-4 py-1.5 rounded-full text-xs text-gray-700 font-medium">
                                {formatDateDivider(currentDate)}
                            </div>
                        </div>
                    )}
                    <div className={`relative flex ${message.isUser ? "justify-end" : "justify-start"} group overflow-visible`}>
                        {/* Call as render helper (not <MessageRenderer />) so nested function
                            components are not treated as a new type on every parent render. */}
                        {MessageRenderer({ message })}
                    </div>
                </React.Fragment>
            );
        });
    }, [messages]);

    const scrollMessagesToEnd = useCallback((behavior = "auto") => {
        requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
        });
    }, []);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        scrollMessagesToEnd("auto");
    }, [messages?.length, scrollMessagesToEnd]);

    // Keep the latest messages visible when the iOS keyboard opens/closes
    useEffect(() => {
        if (!isMobileLayout) return undefined;

        const viewport = window.visualViewport;
        if (!viewport) return undefined;

        const handleViewportChange = () => scrollMessagesToEnd("auto");

        viewport.addEventListener("resize", handleViewportChange);
        viewport.addEventListener("scroll", handleViewportChange);

        return () => {
            viewport.removeEventListener("resize", handleViewportChange);
            viewport.removeEventListener("scroll", handleViewportChange);
        };
    }, [isMobileLayout, scrollMessagesToEnd]);

    useEffect(() => {
        const handleClickOutside = (event) => {

            // Don't close if clicking on file input
            if (event.target.type === "file") {
                return;
            }

            const isOutside = rootRef.current && !rootRef.current.contains(event.target);
            const isNotAttachButton = !attachButtonRef.current || !attachButtonRef.current.contains(event.target);
            const isNotVoiceAttachButton = !voiceAttachButtonRef.current || !voiceAttachButtonRef.current.contains(event.target);
            const isNotVoiceRecorder = !voiceRecorderRef.current || !voiceRecorderRef.current.contains(event.target);


            if (isOutside && isNotAttachButton && isNotVoiceAttachButton && isNotVoiceRecorder) {
                if (showAttachModal) {
                    setShowAttachModal(false);
                }
                if (showVoiceAttachModal) {
                    setShowVoiceAttachModal(false);
                }
                if (showVoice) {
                    onReturnToInput();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showAttachModal, showVoiceAttachModal, showVoice, onReturnToInput]);

    // Close emoji picker when clicking outside
    useEffect(() => {
        if (!showEmoji) return;

        const handler = (e) => {
            const inPicker = emojiRef.current?.contains(e.target);
            const onButton = emojiButtonRef.current?.contains(e.target);
            if (!inPicker && !onButton) {
                setShowEmoji(false);
            }
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showEmoji]);

    useEffect(() => {
        const preventDefaultDrop = (event) => {
            const hasFiles = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === "file");
            if (hasFiles) {
                event.preventDefault();
            }
        };

        window.addEventListener("dragover", preventDefaultDrop);
        window.addEventListener("drop", preventDefaultDrop);

        return () => {
            window.removeEventListener("dragover", preventDefaultDrop);
            window.removeEventListener("drop", preventDefaultDrop);
        };
    }, []);

    const handleComposerDragEnter = useCallback((event) => {
        event.preventDefault();
        const hasFiles = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === "file");
        if (!hasFiles) return;
        dragDepthRef.current += 1;
        setIsFileDragging(true);
        // Close file-selection menus while drag-and-drop is active
        setShowAttachModal?.(false);
        setShowVoiceAttachModal?.(false);
        setShowEmoji(false);
    }, [setShowAttachModal, setShowVoiceAttachModal]);

    const handleComposerDragOver = useCallback((event) => {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
    }, []);

    const handleComposerDragLeave = useCallback((event) => {
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsFileDragging(false);
        }
    }, []);

    const isDroppedImageFile = (file) => {
        if (file?.type?.startsWith("image/")) return true;
        const ext = file?.name?.split(".").pop()?.toLowerCase();
        return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext || "");
    };

    const isDroppedVideoFile = (file) => {
        if (file?.type?.startsWith("video/")) return true;
        const ext = file?.name?.split(".").pop()?.toLowerCase();
        return ["mp4", "webm", "mov", "avi", "mkv", "ogg"].includes(ext || "");
    };

    const handleComposerDrop = useCallback((event) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setIsFileDragging(false);

        const droppedFiles = Array.from(event.dataTransfer?.files || []);
        const imageFiles = droppedFiles.filter(isDroppedImageFile);
        const videoFiles = droppedFiles.filter(isDroppedVideoFile);

        if (imageFiles.length === 0 && videoFiles.length === 0) {
            toast.error("Drop an image or video to send");
            return;
        }

        if (imageFiles.length > 0) {
            onFileSelect?.(imageFiles.length === 1 ? imageFiles[0] : imageFiles, "photo");
        }

        videoFiles.forEach((file) => {
            onFileSelect?.(file, "video");
        });
    }, [onFileSelect]);

    // Video modal: show large player when a video message is opened
    useEffect(() => {
        if (!videoModal) return;
        // Prevent background scrolling while modal open
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // Try to autoplay modal video (in next tick)
        const t = setTimeout(() => {
            try {
                modalVideoRef.current?.play?.().catch(() => {});
            } catch (e) {
                // ignore autoplay errors
            }
        }, 50);
        return () => { clearTimeout(t); document.body.style.overflow = originalOverflow; };
    }, [videoModal]);

    const closeVideoModal = () => {
        try {
            if (modalVideoRef.current) {
                modalVideoRef.current.pause();
                modalVideoRef.current.src = "";
            }
        } catch (err) {
            }
        setVideoModal(null);
        setModalVideoPlaying(false);
        setModalVideoCurrent(0);
    };
    const mobileBottomSafeClass = "";

    return (
        <section
            className="bg-[#F3F0FF] h-full min-h-0 flex flex-col overflow-hidden relative"
            ref={rootRef}
            onDragEnter={handleComposerDragEnter}
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
            onDrop={handleComposerDrop}
        >
            {messageToShare && (
                <ShareMessageModal
                    open={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    message={messageToShare}
                    contacts={allContacts.filter(c => c.user_id !== selectedContactId)}
                    onShareMessage={onShareMessage}
                />
            )}
            {isFileDragging && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-none border-2 border-dashed border-[#7C4DFF] bg-[#7C4DFF]/10 backdrop-blur-[1px]">
                    <div className="rounded-full bg-[#7C4DFF] px-4 py-2 text-sm font-semibold text-white shadow-lg">
                        Drop image or video to send
                    </div>
                </div>
            )}
            {contact ? (
                <>
                    {/* HEADER - Mobile */}
                    <div className="md:hidden flex shrink-0 items-center justify-between sticky top-0 p-3 bg-[#F1F1F1] border-b border-gray-200 z-10">
                        <div className="flex items-center flex-1">
                            <button onClick={onBackMobile} className="mr-3">
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            {contact && (
                                <div className="flex items-center">
                                    <img
                                        src={`${process.env.REACT_APP_API_BASE_URL}${contact?.profile_image}`}
                                        className="h-10 w-10 mr-3 rounded-full object-cover"
                                        alt={contact?.full_name || contact?.name}
                                    />
                                    <div>
                                        <h2 className="font-semibold text-gray-900">{contact?.full_name || contact?.name}</h2>
                                        {safeStatus && (   <div className="status-marquee">
                                            <span
                                                className="status-marquee__text font-base text-xs text-gray-500 cursor-pointer hover:text-purple-600 transition-colors"
                                            >
                                                {safeStatus}
                                            </span>
                                        </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* HEADER - Desktop */}
                    <div className="hidden md:flex bg-[#F1F1F1] border-b border-gray-200 p-4 items-center justify-between sticky top-0 z-10">
                        <div className="flex items-center">
                            {contact && (
                                <>
                                    <img
                                        src={`${process.env.REACT_APP_API_BASE_URL}${contact?.profile_image}`}
                                        className="h-12 w-12 mr-3 rounded-full object-cover"
                                        alt={contact?.full_name || contact?.name}
                                    />
                                    <div>
                                        <h2 className="font-semibold text-gray-900">{contact?.full_name || contact?.name}</h2>
                                       {safeStatus && ( <div className="">
                                            <span
                                                className=" font-base text-sm text-gray-500 cursor-pointer hover:text-purple-600 transition-colors"
                                            >
                                                {safeStatus}
                                            </span>
                                        </div>
                                         )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* MESSAGES */}
                    <div
                        className="mobile-scroll-y flex-1 min-h-0 p-4 md:pb-4 space-y-4"
                        ref={messagesScrollRef}
                    >
                        {renderedMessages}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* INPUT BAR */}
                    <div
                        className={`${showVoice ? "bg-[#F1F1F1]" : "bg-white"} z-10 p-3 md:p-4 shrink-0 transition-colors ${mobileBottomSafeClass}`}
                    >
                        <div className="relative">
                        {/* Upload Progress Bar */}
                        {/* {isUploading && (
                            <div className="mb-3 bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        )}
                        {isUploading && (
                            <div className="text-xs text-gray-600 mb-3 text-center font-medium">
                                Uploading... {uploadProgress}%
                            </div>
                        )} */}

                        <div className="flex items-center space-x-3 relative min-h-[35px]">
                            {showVoice ? (
                                <>
                                    <VoiceRecorder
                                        ref={voiceRecorderRef}
                                        attachButtonRef={voiceAttachButtonRef}
                                        time={voiceTime}
                                        onMicClick={onSendVoiceClick}
                                        onCancel={onReturnToInput}
                                        onAttachmentClick={() =>
                                            setShowVoiceAttachModal(!showVoiceAttachModal)
                                        }
                                    />
                                    <AttachmentModal
                                        open={showVoiceAttachModal}
                                        onClose={() => setShowVoiceAttachModal(false)}
                                        anchorRef={voiceAttachButtonRef}
                                        position="voice"
                                        onAttachPick={handleAttachPick}
                                        onFileSelect={onFileSelect}
                                        selectedContactId={selectedContactId}
                                    />
                                </>
                            ) : (

                                <>
                                    <button
                                        aria-label="Add attachment"
                                        ref={attachButtonRef}
                                        className="md:h-10 md:w-11 w-11 h-8 p-0 rounded-full bg-gray-100 hover:bg-gray-200 flex justify-center items-center relative"
                                        onClick={() => {
                                            setShowAttachModal(!showAttachModal);
                                        }}
                                        title="Click to attach files, images, or documents"
                                    >
                                        <img
                                            src="../chatscreen/plus.svg"
                                            alt="plus"
                                            className="md:w-8 md:h-8 w-6 h-6"
                                        />
                                    </button>

                                    <AttachmentModal
                                        open={showAttachModal}
                                        onClose={() => setShowAttachModal(false)}
                                        anchorRef={attachButtonRef}
                                        position="input"
                                        onAttachPick={handleAttachPick}
                                        onFileSelect={onFileSelect}
                                        selectedContactId={selectedContactId}
                                    />
                                    <input
                                        ref={chatInputRef}
                                        id="chat"
                                        type="text" placeholder="Write Here...."
                                        readOnly={isMobileLayout && showEmoji}
                                        className="pr-20 w-full rounded-[100px] border border-gray-400 bg-white px-4 py-3 text-base text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                                        value={message}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setMessage(val.charAt(0) ? val.charAt(0).toUpperCase() + val.slice(1) : "");
                                        }}
                                        onFocus={() => scrollMessagesToEnd("smooth")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") onSendMessage();
                                        }}
                                    />
                                    <button
                                        type="button"
                                        ref={emojiButtonRef}
                                        className="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        onMouseDown={(e) => {
                                            if (isMobileLayout) e.preventDefault();
                                        }}
                                        onClick={handleEmojiToggle}
                                        aria-expanded={showEmoji}
                                        aria-label="Toggle emoji picker"
                                    >
                                        <img
                                            src="../chatscreen/emoji.svg"
                                            alt="emoji"
                                            className="w-8 h-8"
                                        />
                                    </button>

                                    <EmojiPickerPopover
                                        open={showEmoji}
                                        anchorRef={emojiButtonRef}
                                        containerRef={rootRef}
                                        pickerRef={emojiRef}
                                        onEmojiClick={(emojiData) => {
                                            const char = emojiData?.emoji || emojiData?.native || emojiData?.unified || "";
                                            setMessage((m) => (m || "") + char);
                                            setShowEmoji(false);
                                        }}
                                    />

                                    {/* Conditionally show Send (when there is text) or Mic (when empty) */}
                                    {message && message.trim() ? (
                                        <button
                                            type="button"
                                            className="h-10 w-10 p-0 rounded-full"
                                            onClick={onSendMessage}
                                            title="Send message"
                                        >
                                            <img
                                                src="../chatscreen/send-arrow.svg"
                                                alt="send"
                                                className="w-8 h-8"
                                            />
                                        </button>
                                    ) : (
                                        <button
                                            className="h-10 w-10 p-0 rounded-full"
                                            aria-label="Record voice"
                                            onClick={onMicButton}
                                            title="Record voice"
                                        >
                                            <img
                                                src="../chatscreen/mic.svg"
                                                alt="mic"
                                                className="w-8 h-8"
                                            />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="hidden md:flex flex-col items-center justify-center h-full gap-4 p-4 border-gray-200 border bg-[#F4F3FF]">
                    <div className="h-40 w-40 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
                        <img src="../logo.svg" alt=" PhoneBookPlus Logo" />
                    </div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-gray-500 font-inter text-center">
                         PhoneBookPlus
                    </h1>
                    <p className="text-gray-400">Select a contact to start chatting</p>
                </div>
            )
            }

            {/* Image Preview Modal */}
            {imagePreview && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4"
                    onClick={() => setImagePreview(null)}
                >
                    <div
                        className="relative flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setImagePreview(null)}
                            className="absolute top-3 right-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                            aria-label="Close image preview"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-gray-900"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        {/* Image */}
                        <img
                            src={imagePreview}
                            alt="Full preview"
                            className="max-w-[90vw] max-h-[60vh] min-h-[200px] min-w-[200px] object-contain rounded-lg shadow-2xl"
                            onError={(e) => {
                                e.target.src = "../chatscreen/sample-image.jpg";
                            }}
                        />

                        {/* Download Button */}
                        <a
                            href={imagePreview}
                            download="image"
                            className="absolute bottom-3 left-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors shadow-lg"
                            title="Download image"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-gray-900"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v4a2 2 0 002 2h12a2 2 0 002-2v-4m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                            </svg>
                        </a>
                    </div>
                </div>
            )}

            {viewOncePreview && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4"
                    onClick={() => setViewOncePreview(null)}
                >
                    <div
                        className="relative flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setViewOncePreview(null)}
                            className="absolute top-3 right-2 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
                            aria-label="Close view once image"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-gray-900"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        <img
                            src={viewOncePreview.src}
                            alt="View once"
                            style={{ filter: blurViewOnce ? "blur(20px)" : "none" }}
                            className="max-w-[90vw] max-h-[60vh] min-h-[200px] min-w-[200px] object-contain rounded-lg shadow-2xl"
                            onError={(e) => {
                                e.target.src = "../chatscreen/sample-image.jpg";
                            }}
                            draggable={false}
                        />
                        
                        {blurViewOnce && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="text-2xl font-bold text-white bg-black/70 px-6 py-3 rounded-xl">Tab inactive</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Video Modal (themed) */}
            {videoModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
                    onClick={closeVideoModal}
                >
                    <div className="w-full max-w-4xl rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-[#12051f]" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 bg-[#fff] border-b border-white/10">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-black truncate">{videoModal?.title || videoModal?.src?.split('/').pop() || 'Video'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {videoModal?.src && !videoModal?.isViewOnce && (
                                    <a
                                        href={videoModal.src}
                                        download
                                        className="px-3 py-2 rounded-full bg-black/10 hover:bg-white/20 text-black text-xs font-medium"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Download
                                    </a>
                                )}
                                <button
                                    onClick={closeVideoModal}
                                    className="w-9 h-9 rounded-full bg-black/10 hover:bg-white/20 flex items-center justify-center"
                                    aria-label="Close video"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="bg-black relative">
                            <video
                                ref={modalVideoRef}
                                src={videoModal.src}
                                poster={videoModal.poster}
                                className="w-full max-h-[72vh] object-contain bg-black"
                                style={{ filter: videoModal.isViewOnce && blurViewOnce ? "blur(20px)" : "none" }}
                                playsInline
                                preload="metadata"
                                controlsList={videoModal.isViewOnce ? "nodownload" : undefined}
                            />
                            {videoModal.isViewOnce && blurViewOnce && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm font-medium px-4 text-center">
                                    Content hidden while app is in background
                                </div>
                            )}
                        </div>

                        <div className="px-4 py-4 bg-[#fff] border-t border-white/10 text-white">
                            <div className="flex items-center gap-3">
                              

                                <div className="flex-1 min-w-0">
                                    <input
                                        type="range"
                                        min="0"
                                        max={modalVideoDuration || 0}
                                        step="0.01"
                                        value={modalVideoCurrent}
                                        onChange={(e) => {
                                            const v = modalVideoRef.current;
                                            if (!v || !Number.isFinite(v.duration)) return;
                                            v.currentTime = Number(e.target.value);
                                            setModalVideoCurrent(Number(e.target.value));
                                        }}
                                        className="w-full accent-[#7C4DFF]"
                                    />
                                    <div className="flex items-center justify-between mt-1 text-xs text-black/70">
                                        <span>{(function(s){ if(!Number.isFinite(s)) return '0:00'; const m=Math.floor(s/60); const sec=Math.floor(s%60); return `${m}:${String(sec).padStart(2,'0')}`})(modalVideoCurrent)}</span>
                                        <button
                                            onClick={() => {
                                                try {
                                                    const v = modalVideoRef.current;
                                                    if (!v) return;
                                                    if (v.paused) {
                                                        v.play().catch(() => {});
                                                    } else {
                                                        v.pause();
                                                    }
                                                } catch (_) {}
                                            }}
                                            className="w-8 h-8 rounded-full bg-[#7C4DFF] hover:bg-[#8f63ff] flex items-center justify-center shadow-lg"
                                            aria-label="Play/Pause"
                                        >
                                            {modalVideoPlaying ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            )}
                                        </button>
                                        <span>{(function(s){ if(!Number.isFinite(s)) return '0:00'; const m=Math.floor(s/60); const sec=Math.floor(s%60); return `${m}:${String(sec).padStart(2,'0')}`})(modalVideoDuration)}</span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => {
                                        const v = modalVideoRef.current;
                                        if (!v) return;
                                        const nextMuted = !v.muted;
                                        v.muted = nextMuted;
                                        setModalVideoMuted(nextMuted);
                                    }}
                                    className="w-8 h-8 rounded-full  flex items-center justify-center"
                                    aria-label="Mute/Unmute"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-15 h-15 text-[#7C4DFF]" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 10v4h4l5 4V6L7 10H3Zm13.5 2a4.5 4.5 0 0 0-2.25-3.9v7.8A4.5 4.5 0 0 0 16.5 12Z" />
                                    </svg>
                                </button>

                                <div className="w-28">
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={modalVideoVolume}
                                        onChange={(e) => {
                                            const v = modalVideoRef.current;
                                            const vol = Math.max(0, Math.min(1, Number(e.target.value)));
                                            setModalVideoVolume(vol);
                                            if (v) {
                                                v.volume = vol;
                                                v.muted = vol === 0;
                                            }
                                        }}
                                        className="w-full accent-[#7C4DFF]"
                                        aria-label="Volume"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </section>
    );
}
 